import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { type PermissionCode, type RoleEntity } from '@magimp/shared';
import { useRolePermissions, useSetRolePermissions } from '@magimp/shared-hooks';
import { LoadingState, ErrorState } from '../../../ui/index.js';
import { useToastStore } from '../../../stores/toast.js';
import { errorMessage } from '../../../api/errors.js';
import { PermissionGrid } from './PermissionGrid.js';

export function RolePermissionsTab({
  role,
  disabled,
  onDirtyChange,
  onBusyChange,
}: {
  role: RoleEntity;
  disabled: boolean;
  onDirtyChange: (dirty: boolean) => void;
  onBusyChange: (busy: boolean) => void;
}) {
  const { t } = useTranslation(['roles', 'common']);
  const permsQ = useRolePermissions(role.id);
  const setPerms = useSetRolePermissions(role.id);
  const showToast = useToastStore((s) => s.show);

  // Bubble PUT pending state up to the drawer guard so close attempts during
  // a save are suppressed. The grid's own `onDirtyChange` covers the dirty
  // half (it fires `false` on unmount, including the remount triggered by
  // `key={data.permsVersion}` after a successful save).
  useEffect(() => {
    onBusyChange(setPerms.isPending);
  }, [setPerms.isPending, onBusyChange]);
  useEffect(() => () => onBusyChange(false), [onBusyChange]);
  useEffect(() => () => onDirtyChange(false), [onDirtyChange]);

  if (permsQ.isPending) return <LoadingState />;
  if (permsQ.error)
    return <ErrorState title={t('common:states.loadError')} text={errorMessage(permsQ.error)} />;

  // WHY: TanStack `useQuery` does not narrow `data` to non-null even after
  // an `isPending && error === null` gate, so the assertion is needed here.
  const data = permsQ.data!;

  const submit = async (codes: PermissionCode[]) => {
    await setPerms.mutateAsync(
      { permissions: codes, ifVersion: data.permsVersion },
      {
        onSuccess: () => showToast({ tone: 'success', text: t('roles:toasts.permissionsUpdated') }),
      },
    );
  };

  return (
    // DANGER: `PermissionGrid` initialises checkbox state from `initial` only
    //   on mount (a `useState(() => new Set(initial))`), so a stale-write
    //   refetch that updates `data.items` would otherwise leave the user's
    //   prior selections in place — and the next retry could send them with
    //   the freshly-captured `permsVersion`, silently overwriting the
    //   concurrent change the fence just protected. Keying by
    //   `permsVersion` forces a remount on every successful write OR
    //   stale-409 invalidation so the grid always reflects the live row.
    //   The remount also re-seeds the grid's internal baseline, which makes
    //   its `onDirtyChange(false)` (fired on the old instance's unmount)
    //   the natural reset signal for the drawer-level guard.
    <PermissionGrid
      key={data.permsVersion}
      initial={data.items}
      disabled={disabled}
      pending={setPerms.isPending}
      onSubmit={submit}
      onError={(err) => showToast({ tone: 'error', text: errorMessage(err) })}
      onDirtyChange={onDirtyChange}
    />
  );
}
